import { useEffect } from "react";

const LAYER_ID = "eh-christmas-snow-layer";
const SNOWFLAKE_COUNT = 52;
const SNOW_MOTION_VERSION = "5";

function queryPrimaryMainEl(): Element | null {
  return (
    document.querySelector("main.eh-layout-main") ||
    document.querySelector("main#main") ||
    document.querySelector("main.main-area") ||
    document.querySelector("main:not([hidden])")
  );
}

const SNOWFLAKE_SIZES = ["s", "m", "l"] as const;

function applySnowflakeMotionStyles(flake: HTMLElement, index: number): void {
  const size = SNOWFLAKE_SIZES[index % SNOWFLAKE_SIZES.length];
  const sizeFactor = size === "l" ? 1.22 : size === "s" ? 0.82 : 1;
  const driftX = (12 + Math.random() * 80) * (Math.random() > 0.5 ? 1 : -1);

  flake.style.setProperty("--eh-snow-drift-x", `${driftX}px`);
  flake.style.setProperty(
    "--eh-snow-spin",
    index % 4 === 0 ? "0deg" : `${180 + Math.random() * 540}deg`,
  );
  flake.style.animationDuration = `${(8 + Math.random() * 20) * sizeFactor}s`;
  flake.style.animationDelay = `${-Math.random() * 22}s`;
  flake.style.animationTimingFunction = "linear";
}

function buildSnowInner(): HTMLDivElement {
  const inner = document.createElement("div");
  inner.className = "eh-global-snow";
  inner.dataset.motion = SNOW_MOTION_VERSION;
  for (let i = 0; i < SNOWFLAKE_COUNT; i += 1) {
    const flake = document.createElement("span");
    flake.className = `eh-snowflake eh-snowflake--${SNOWFLAKE_SIZES[i % SNOWFLAKE_SIZES.length]}`;
    flake.style.left = `${Math.random() * 100}%`;
    applySnowflakeMotionStyles(flake, i);
    inner.appendChild(flake);
  }
  return inner;
}

/** Schneefall + optionale Weihnachts-Dekoration (Dezember / Dev-Simulation). */
export function useChristmasEffects(
  seasonActive: boolean,
  options: { showSnow?: boolean; showSeasonDecor?: boolean } = {},
): void {
  const showSnow = options.showSnow ?? seasonActive;
  const showSeasonDecor = options.showSeasonDecor ?? seasonActive;

  useEffect(() => {
    if (typeof document === "undefined") return;

    let clipRaf = 0;
    let resizeObserver: ResizeObserver | null = null;

    const disconnectObserver = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const updateClip = () => {
      const layer = document.getElementById(LAYER_ID);
      if (!layer || layer.hasAttribute("hidden")) return;

      const main = queryPrimaryMainEl();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!main) {
        layer.style.clipPath = "none";
        return;
      }

      const rect = main.getBoundingClientRect();
      if (rect.width < 4 || rect.height < 4) {
        layer.style.clipPath = "none";
        return;
      }

      const pad = 6;
      const left = Math.max(0, rect.left - pad);
      const top = Math.max(0, rect.top - pad);
      const right = Math.min(vw, rect.right + pad);
      const bottom = Math.min(vh, rect.bottom + pad);

      layer.style.clipPath = [
        "polygon(evenodd,",
        `0px 0px, ${vw}px 0px, ${vw}px ${vh}px, 0px ${vh}px, 0px 0px,`,
        `${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px ${top}px)`,
      ].join(" ");
    };

    const scheduleClip = () => {
      if (clipRaf) cancelAnimationFrame(clipRaf);
      clipRaf = requestAnimationFrame(() => {
        clipRaf = 0;
        updateClip();
      });
    };

    const bindMainObserver = () => {
      disconnectObserver();
      const main = queryPrimaryMainEl();
      if (!main) return;
      resizeObserver = new ResizeObserver(() => scheduleClip());
      resizeObserver.observe(main);
    };

    const teardownSnow = () => {
      document.body.classList.remove("eh-christmas-snow");
      const layer = document.getElementById(LAYER_ID);
      layer?.setAttribute("hidden", "");
      layer?.style.removeProperty("clip-path");
      disconnectObserver();
      window.removeEventListener("resize", scheduleClip);
      window.removeEventListener("scroll", scheduleClip, true);
      if (clipRaf) cancelAnimationFrame(clipRaf);
    };

    const teardownAll = () => {
      document.body.classList.remove("eh-christmas-season", "eh-christmas-snow");
      teardownSnow();
    };

    if (!seasonActive) {
      teardownAll();
      return teardownAll;
    }

    document.body.classList.toggle("eh-christmas-season", showSeasonDecor);

    if (!showSnow) {
      document.body.classList.remove("eh-christmas-snow");
      teardownSnow();
      return () => {
        document.body.classList.remove("eh-christmas-season", "eh-christmas-snow");
      };
    }

    document.body.classList.add("eh-christmas-snow");

    let layer = document.getElementById(LAYER_ID);
    if (!layer) {
      layer = document.createElement("div");
      layer.id = LAYER_ID;
      layer.setAttribute("aria-hidden", "true");
      layer.appendChild(buildSnowInner());
      const anchor = document.body.firstElementChild;
      if (anchor) document.body.insertBefore(layer, anchor);
      else document.body.appendChild(layer);
    } else {
      const inner = layer.querySelector(".eh-global-snow") as HTMLElement | null;
      if (!inner || inner.dataset.motion !== SNOW_MOTION_VERSION) {
        inner?.remove();
        layer.appendChild(buildSnowInner());
      }
    }

    layer.removeAttribute("hidden");
    bindMainObserver();
    scheduleClip();

    window.addEventListener("resize", scheduleClip, { passive: true });
    window.addEventListener("scroll", scheduleClip, { passive: true, capture: true });

    return () => {
      document.body.classList.remove("eh-christmas-season", "eh-christmas-snow");
      teardownSnow();
    };
  }, [seasonActive, showSnow, showSeasonDecor]);
}
